/**
 * Block buildings — the set-dressing mass that rings the playable area and
 * gives every sightline a believable backdrop. Facades are only built on the
 * sides that can actually be seen; the rest is a cheap chamfered core.
 *
 * owner: level
 */
import { chamferBox, xform } from '../Geo.js';
import { wall } from './Walls.js';
import {
  roofClutter, roofSurface, drainpipe, laundryLine, wireBundle, awning,
  antenna, chimneyStack, rooflineClutter,
} from './Detail.js';

/*
 * Facade materials. `brick_wall` is deliberately absent: it is a saturated red
 * English-bond texture, and three red-brick blocks around a sun-bleached
 * Levantine square read as a different game — plus the bond tiles visibly at
 * the 1.7 uvScale these facades use. Brick survives only where it belongs:
 * the exposed core under blown plaster, and chimney stacks.
 */
const WALL_MATS = ['plaster_wall', 'stucco', 'concrete_wall_painted', 'plaster_wall', 'stucco', 'concrete_wall'];

/** Even window rhythm along a facade, avoiding the corners. */
export function windowRow(length, { w = 1.05, h = 1.55, y = 1.05, gap = 2.9, arch = false, glass = 'dirty', margin = 1.2 }) {
  const usable = length - margin * 2;
  if (usable < w) return [];
  const n = Math.max(1, Math.round(usable / gap));
  const step = usable / n;
  const out = [];
  for (let i = 0; i < n; i++) {
    const x = -usable / 2 + step * (i + 0.5);
    out.push({ x, y, w, h, arch, glass, sill: true, lintel: !arch });
  }
  return out;
}

export function blockBuilding(w, spec) {
  const rng = w.rng;
  const {
    x0, z0, x1, z1, base = 0, storeys = 3, storeyH = 3.4,
    mat = null, faces = { n: true, s: true, e: true, w: true },
    parapet = true, clutter = true, roofMat = 'concrete_floor',
    balconies = 0, groundOpenings = null, thickness = 0.36, seedTint = null,
  } = spec;

  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  const W = X1 - X0, D = Z1 - Z0;
  const H = storeys * storeyH;
  const m = mat || rng.pick(WALL_MATS);
  const tint = seedTint || [rng.range(0.86, 1.08), rng.range(0.84, 1.04), rng.range(0.74, 0.98)];

  /* core mass */
  const core = chamferBox(W - 0.2, H, D - 0.2, 0.07);
  w.batch.add(core, 'concrete_wall', {
    matrix: xform([(X0 + X1) / 2, base + H / 2, (Z0 + Z1) / 2], 0),
    surface: 'concrete', uvScale: 2.2, tint: [tint[0] * 0.72, tint[1] * 0.7, tint[2] * 0.68],
  });
  core.dispose();
  // up to the *top of the roof deck*, not the top of the core: the deck is what
  // you would stand on, and a proxy that stops 0.3 m short lets a body sink
  // into it. w.zone() gives surfaceAt() the same answer.
  w.blockerBox(X0, base, Z0, X1, base + H + 0.3, Z1, 'concrete');
  w.zone(X0, Z0, X1, Z1, base + H + 0.3, 'concrete');

  /* roof deck + parapet */
  const deck = chamferBox(W, 0.3, D, 0.05);
  w.batch.add(deck, roofMat, { matrix: xform([(X0 + X1) / 2, base + H + 0.15, (Z0 + Z1) / 2], 0), surface: 'concrete', uvScale: 2.4, tint });
  deck.dispose();
  let ph0Ref = 0.9;
  if (parapet) {
    /*
     * A parapet run at ONE height around a rectangular block draws a dead
     * straight line against the sky, and `CRITIC.md` §3 calls that an outright
     * fail. Each edge is now split into two or three lengths of different
     * height, one length in eight is knocked out entirely, and the coping steps
     * with them — so the roofline breaks up before a single aerial is added.
     */
    const ph0 = rng.range(0.62, 1.15);
    ph0Ref = ph0;
    const deckY = base + H + 0.3;
    const runEdge = (along, fixed, len, isX) => {
      // 4–6 lengths, not 2–3: a 16 m facade split in two still gives an 8 m
      // straight run, and 8 m of unbroken coping is a straight line at any
      // distance the block is ever seen from.
      const segs = rng.int(4, 6);
      let cursor = -len / 2;
      let prevH = null;
      for (let s = 0; s < segs; s++) {
        const frac = s === segs - 1 ? (len / 2 - cursor) : (len / segs) * rng.range(0.55, 1.5);
        const sl = Math.min(frac, len / 2 - cursor);
        if (sl < 0.4) break;
        const cMid = cursor + sl / 2;
        const start = cursor;
        cursor += sl;
        const cx = isX ? along + cMid : fixed;
        const cz = isX ? fixed : along + cMid;
        if (rng() < 0.14) {                          // blown-out length
          // leave the stumps of the wall that came down, not a clean gap
          for (let k = 0; k < 3; k++) {
            const sh = ph0 * rng.range(0.10, 0.42);
            const su = start + sl * rng.range(0.08, 0.92);
            const sx = isX ? along + su : fixed + rng.range(-0.05, 0.05);
            const sz = isX ? fixed + rng.range(-0.05, 0.05) : along + su;
            const sw = rng.range(0.25, 0.7);
            const g = isX ? chamferBox(sw, sh, 0.28, 0.022) : chamferBox(0.28, sh, sw, 0.022);
            w.batch.add(g, m, {
              matrix: xform([sx, deckY + sh / 2, sz], [rng.range(-0.06, 0.06), 0, rng.range(-0.07, 0.07)]),
              surface: 'concrete', uvScale: 1.1,
              tint: [tint[0] * rng.range(0.78, 0.98), tint[1] * rng.range(0.78, 0.96), tint[2] * rng.range(0.76, 0.94)],
            });
            g.dispose();
          }
          prevH = 0;
          continue;
        }
        // step hard against the previous length: a ±10 % wobble is not a step
        let ph = ph0 * rng.range(0.55, 1.75);
        if (prevH !== null && Math.abs(ph - prevH) < 0.22) ph = prevH + (rng.sign() * rng.range(0.24, 0.6));
        ph = Math.max(0.34, Math.min(2.4, ph));
        const g = isX ? chamferBox(sl, ph, 0.28, 0.04) : chamferBox(0.28, ph, sl, 0.04);
        w.batch.add(g, m, { matrix: xform([cx, deckY + ph / 2, cz], 0), surface: 'concrete', uvScale: 1.3, tint });
        g.dispose();
        // coping: missing on one length in six, so the top edge is not one line
        if (rng() > 0.16) {
          const cap = isX ? chamferBox(sl, 0.10, 0.42, 0.03) : chamferBox(0.42, 0.10, sl, 0.03);
          w.batch.add(cap, 'concrete_wall', { matrix: xform([cx, deckY + ph + 0.05, cz], 0), surface: 'concrete', uvScale: 0.8 });
          cap.dispose();
        }
        // a squat pier at the joint between two lengths — the classic Levantine
        // parapet detail, and it puts a vertical every few metres
        if (rng() < 0.45) {
          const pierH = ph + rng.range(0.25, 0.75);
          const pw = rng.range(0.34, 0.5);
          const pu = start + (rng() < 0.5 ? 0.12 : sl - 0.12);
          const qx = isX ? along + pu : fixed;
          const qz = isX ? fixed : along + pu;
          const p = chamferBox(isX ? pw : 0.4, pierH, isX ? 0.4 : pw, 0.035);
          w.batch.add(p, m, { matrix: xform([qx, deckY + pierH / 2, qz], 0), surface: 'concrete', uvScale: 1.1, tint });
          p.dispose();
          const pc = chamferBox(isX ? pw + 0.14 : 0.52, 0.09, isX ? 0.52 : pw + 0.14, 0.026);
          w.batch.add(pc, 'concrete_wall', { matrix: xform([qx, deckY + pierH + 0.045, qz], 0), surface: 'concrete', uvScale: 0.6 });
          pc.dispose();
        }
        prevH = ph;
      }
    };
    runEdge((X0 + X1) / 2, Z0 + 0.14, W, true);
    runEdge((X0 + X1) / 2, Z1 - 0.14, W, true);
    runEdge((Z0 + Z1) / 2, X0 + 0.14, D - 0.56, false);
    runEdge((Z0 + Z1) / 2, X1 - 0.14, D - 0.56, false);
  }

  /*
   * Chamfered corner quoins.
   *
   * `light_goldenhour` (630–645, 200–620) reported a building corner "stepping
   * straight from lit cream to black with no chamfer highlight". It was right:
   * every facade is a separate punched panel whose ENDS butt square, so the
   * vertical arris where two facades meet had no chamfer at all — the panel's
   * 4.5 cm bevel only ever reached its front and back faces. A quoin column at
   * each corner puts a real chamfer on the one edge in a building that always
   * sits against the sky.
   */
  for (const [qx, qz] of [[X0, Z0], [X1, Z0], [X0, Z1], [X1, Z1]]) {
    const q = chamferBox(0.52, H + 0.26, 0.52, 0.055);
    w.batch.add(q, m, {
      matrix: xform([qx + (qx === X0 ? 0.14 : -0.14), base + (H + 0.26) / 2, qz + (qz === Z0 ? 0.14 : -0.14)], 0),
      surface: 'concrete', uvScale: 1.1,
      tint: [tint[0] * 1.05, tint[1] * 1.04, tint[2] * 1.01],
    });
    q.dispose();
  }

  /* facades */
  // wall(a→b) puts its outward face along (-dz, dx); pick the winding that makes
  // "outward" point away from the building on each side.
  const sides = {
    n: { a: [X1, Z0], b: [X0, Z0], len: W, nx: 0, nz: -1 },
    s: { a: [X0, Z1], b: [X1, Z1], len: W, nx: 0, nz: 1 },
    w: { a: [X0, Z0], b: [X0, Z1], len: D, nx: -1, nz: 0 },
    e: { a: [X1, Z1], b: [X1, Z0], len: D, nx: 1, nz: 0 },
  };
  for (const key of Object.keys(sides)) {
    if (!faces[key]) continue;
    const s = sides[key];
    const off = thickness / 2 - 0.02;
    const a = [s.a[0] + s.nx * off, s.a[1] + s.nz * off];
    const b = [s.b[0] + s.nx * off, s.b[1] + s.nz * off];
    for (let f = 0; f < storeys; f++) {
      const isGround = f === 0;
      const openings = isGround && groundOpenings
        ? groundOpenings(s.len)
        : windowRow(s.len, {
          w: rng.range(0.9, 1.2), h: isGround ? 1.9 : 1.55,
          y: isGround ? 0.9 : 0.95, gap: rng.range(2.6, 3.4),
          arch: !isGround && rng() < 0.3,
        });
      wall(w, {
        a, b, base: base + f * storeyH, height: storeyH, thickness,
        mat: m, openings, tint, uvScale: 1.7,
        // reveal lining only on the two storeys you can ever get close to:
        // it is one extra merged piece per window and there are ~900 windows
        reveal: f < 2,
        plinth: isGround ? { h: 0.4, proj: 0.06 } : false,
        band: f < storeys - 1 ? { y: storeyH - 0.12, h: 0.14, proj: 0.08 } : null,
        cornice: f === storeys - 1 ? { y: storeyH, proj: 0.22, h: 0.28, side: 'one' } : null,
        collide: false, damage: isGround ? rng.int(0, 2) : (rng() < 0.35 ? 1 : 0),
      });
    }
    // drainpipe at one corner
    if (rng() < 0.7) {
      const t = rng() < 0.5 ? 0.06 : 0.94;
      const px = a[0] + (b[0] - a[0]) * t + s.nx * 0.12;
      const pz = a[1] + (b[1] - a[1]) * t + s.nz * 0.12;
      drainpipe(w, { x: px, z: pz, y0: base + 0.05, y1: base + H + 0.4, rotY: Math.atan2(s.nx, s.nz) });
    }
  }

  /* balconies + laundry on the inward face */
  for (let i = 0; i < balconies; i++) {
    const key = balconies > 0 && faces.n ? 'n' : 's';
    const s = sides[key];
    const t = (i + 1) / (balconies + 1);
    const px = s.a[0] + (s.b[0] - s.a[0]) * t;
    const pz = s.a[1] + (s.b[1] - s.a[1]) * t;
    const f = rng.int(1, Math.max(1, storeys - 1));
    const y = base + f * storeyH + 0.1;
    const g = chamferBox(2.2, 0.19, 1.0, 0.035);
    w.batch.add(g, 'concrete_floor', { matrix: xform([px + s.nx * 0.5, y, pz + s.nz * 0.5], 0), surface: 'concrete', uvScale: 1.2 });
    g.dispose();
    const bal = chamferBox(2.2, 0.85, 0.10, 0.028);
    w.batch.add(bal, 'metal_rusted', { matrix: xform([px + s.nx * 0.98, y + 0.5, pz + s.nz * 0.98], Math.atan2(s.nx, s.nz)), surface: 'metal', uvScale: 0.5 });
    bal.dispose();
    if (rng() < 0.8) {
      laundryLine(w,
        [px - 1.0 + s.nx * 0.7, y + 1.5, pz + s.nz * 0.7],
        [px + 1.0 + s.nx * 0.7, y + 1.6, pz + s.nz * 0.7],
        { items: rng.int(2, 4), sag: 0.22 });
    }
  }

  /* ground-floor awnings on the inward face */
  if (spec.awnings) {
    for (const [ax, az, ar, aw] of spec.awnings) {
      awning(w, { x: ax, z: az, y: base + 2.95, rotY: ar, width: aw, depth: 1.35, drop: 0.42, poles: true, tint: [rng.range(0.7, 1.1), rng.range(0.6, 0.95), rng.range(0.5, 0.85)] });
    }
  }

  /*
   * "Rooftops are bare slabs … no AC units, no vents, no aerials, no ducting, no
   * gravel, no puddles, no debris. Every roof in the build is like this."
   *
   * Correct, and it was every roof because only two roofs in the map were dressed
   * by hand in `Level.js`. Both treatments now live in the kit and every
   * `blockBuilding` gets them: the deck treatment first (gravel ballast, tar
   * seams, ponding stains, silver patch repairs), then the furniture. Every
   * piece lands in a batch group that already exists, so this is triangles, not
   * draw calls.
   */
  const top = base + H + 0.3;
  roofSurface(w, { x0: X0 + 0.5, z0: Z0 + 0.5, x1: X1 - 0.5, z1: Z1 - 0.5, y: top, patches: 5 });
  if (clutter) roofClutter(w, { x0: X0 + 0.6, z0: Z0 + 0.6, x1: X1 - 0.6, z1: Z1 - 0.6, y: top, density: 1.15 });
  /*
   * The roofline pass. `roofClutter` above dresses the DECK and every item it
   * places is under 1.1 m and at least 1.6 m inboard, so a 0.6–1.6 m parapet
   * hides all of it from any camera at or below roof level — which is every
   * gameplay camera. This is what actually reaches the sky.
   */
  if (parapet) {
    rooflineClutter(w, {
      x0: X0, z0: Z0, x1: X1, z1: Z1, y: top,
      parapetH: ph0Ref * 1.35, density: spec.rooflineDensity ?? 1.0,
      edges: spec.rooflineEdges ?? 'nsew', solid: false,
      maxItems: spec.rooflineMax ?? 12,
    });
  }
  // one tall thing per roof away from the edge, so the mass has an interior spike
  if (rng() < 0.7) {
    const px = rng.range(X0 + 1.4, X1 - 1.4), pz = rng.range(Z0 + 1.4, Z1 - 1.4);
    if (rng() < 0.55) antenna(w, { x: px, y: top, z: pz, h: rng.range(3.0, 6.4), arms: rng.int(3, 6) });
    else chimneyStack(w, { x: px, y: top, z: pz, h: rng.range(1.4, 2.6), wdt: rng.range(0.5, 0.8), solid: false });
  }
  return { X0, X1, Z0, Z1, top, H };
}

/** Wire a pole/roof-to-roof cable run so the sky is never empty. */
export function powerRun(w, points, opts = {}) {
  for (let i = 0; i < points.length - 1; i++) {
    wireBundle(w, points[i], points[i + 1], { count: opts.count ?? 3, sag: opts.sag ?? 0.8, spread: opts.spread ?? 0.14 });
  }
}
